import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, Stack, Typography } from '@mui/material';

// Заміна таблиці на вузьких екранах. Таблиця на 10 колонок у 390px не
// читається навіть із горизонтальним скролом, тому ключові сторінки
// показують кожен рядок карткою: заголовок, статус, кілька полів і дії.
// Сторінка лише мапить свої дані у цю форму - верстка лишається тут.

export interface MobileCardField {
  label: string;
  value: ReactNode;
  // Поле на всю ширину картки замість половини (довгі адреси, коментарі).
  wide?: boolean;
}

export interface MobileCardItem {
  id: string;
  title: ReactNode;
  // Праворуч від заголовка: статус, чип, сума - те, за чим шукають очима.
  badge?: ReactNode;
  fields?: MobileCardField[];
  actions?: ReactNode;
  onClick?: () => void;
}

interface Props {
  items: MobileCardItem[];
  emptyText?: string;
}

const CardPaper = styled(Paper, {
  shouldForwardProp: prop => prop !== 'clickable'
})<{ clickable: boolean }>(({ theme, clickable }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  cursor: clickable ? 'pointer' : 'default',
  '&:active': clickable
    ? { backgroundColor: theme.palette.action.hover }
    : undefined
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  lineHeight: 1.4
}));

const FieldValue = styled(Typography)({
  fontSize: '0.875rem',
  wordBreak: 'break-word'
}) as typeof Typography;

const MobileCardList = ({ items, emptyText }: Props) => {
  if (!items.length) {
    // Без emptyText нічого не малюємо: у частини сторінок є власний блок
    // порожнього стану, і два підписи поспіль виглядали б помилкою.
    if (!emptyText) return null;

    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color='text.secondary'>{emptyText}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {items.map(item => (
        <CardPaper
          clickable={Boolean(item.onClick)}
          elevation={0}
          key={item.id}
          onClick={item.onClick}
        >
          <Stack
            direction='row'
            spacing={1}
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {item.title}
            </Typography>
            {item.badge}
          </Stack>

          {Boolean(item.fields?.length) && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 1,
                mt: 1
              }}
            >
              {item.fields?.map(field => (
                <Box
                  key={field.label}
                  sx={{ gridColumn: field.wide ? '1 / -1' : 'auto' }}
                >
                  <FieldLabel>{field.label}</FieldLabel>
                  {/* div, а не p: у полі може бути Chip чи інший блок */}
                  <FieldValue component='div'>{field.value}</FieldValue>
                </Box>
              ))}
            </Box>
          )}

          {Boolean(item.actions) && (
            <Stack
              direction='row'
              // Дій буває до п'яти - без переносу вони вилазять за край
              // картки. gap замість spacing, бо spacing не працює з
              // перенесеними рядками.
              sx={{
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                gap: 0.5,
                mt: 1
              }}
              // Клік по кнопці всередині картки не має відкривати картку.
              onClick={event => event.stopPropagation()}
            >
              {item.actions}
            </Stack>
          )}
        </CardPaper>
      ))}
    </Stack>
  );
};

export default MobileCardList;
